import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { useLoadScript, Autocomplete } from '@react-google-maps/api'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/material_blue.css'
import 'flatpickr/dist/flatpickr.css'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { getJobById, createJob, updateJob, getSkills, getUsersListForJob } from '../_requests'
import Select from 'react-select'
import { toast } from 'react-toastify'

// NOTE: react-select expects options in { value, label } shape when used with the default components.
// The old `OptionType` import from dayjs was incorrect and caused type mismatches.
type OptionType = {
  value: string
  label: string
}

// --- Types ---
interface SalaryRange {
  min: number
  max: number
}

interface LocationItem {
  id: string
  latitute: string
  longitute: string
  location_name: string
  postcode: string
}

interface JobPost {
  id: number
  title: string
  description: string
  job_type: string
  vacancies: string
  locations: LocationItem[]
  salary: string // JSON stringified {min, max}
  compensation?: { min: number; max: number }
  compensation_min?: number
  compensation_max?: number
  max_experience: string
  min_experience: string
  experience?: { min: string; max: string }
  experience_min?: string
  experience_max?: string
  timing_from?: string
  timing_to?: string
  timing?: string
  status?: string
  skills: { id: number; name: string; category: string | null; status: string }[]
  created_by?: {
    id: number
    first_name: string
    last_name: string
    email: string
    business_name?: string
    profile_picture_url?: string
    phone?: string
  }
}

interface JobFormData {
  title: string
  description: string
  job_type: string
  vacancies: string
  locations: LocationItem[]
  salary: string
  experience_min: string
  experience_max: string
  startTime: Date | null
  endTime: Date | null,
  skills: string[]
  status: string
}


const mapLibraries: any = ['places']

const JobAddPage = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const autocompleteRefs = useRef<Map<string, any>>(new Map())
  // const editorRef = useRef<any>(null)
  const isMountedRef = useRef(true)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: mapLibraries,
  })

  const [isEditing, setIsEditing] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isFormLoaded, setIsFormLoaded] = useState(false)
  // Removed old skills states

  const initialFormState: JobFormData = {
    title: '',
    description: '',
    job_type: '',
    vacancies: '',
    locations: [{ id: 'default', latitute: '', longitute: '', location_name: '', postcode: '' }],
    salary: '',
    experience_max: '',
    experience_min: '',
    startTime: null,
    endTime: null,
    skills: [],
    status: 'DRAFT'
  }

  const [formData, setFormData] = useState(initialFormState)
  const [errors, setErrors] = useState<any>({})
  const [editorMounted, setEditorMounted] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<OptionType | null>(null)
const [userData, setUserData] = useState<any[]>([])
  const [selectedSkills, setSelectedSkills] = useState<OptionType[] | null>(null)
  const [skillsOptions, setSkillsOptions] = useState<OptionType[]>([])
  const [skillsLoading, setSkillsLoading] = useState(false)

  // Parse salary JSON for editing
  const parseSalaryRange = (salaryStr: string | any): { minSalary: string; maxSalary: string } => {
    let range: SalaryRange
    if (typeof salaryStr === 'string') {
      try {
        range = JSON.parse(salaryStr) as SalaryRange
      } catch {
        // Backward compat - treat as single value
        return { minSalary: salaryStr, maxSalary: salaryStr }
      }
    } else if (typeof salaryStr === 'object' && salaryStr !== null && 'min' in salaryStr) {
      range = salaryStr as SalaryRange
    } else {
      return { minSalary: '', maxSalary: '' }
    }
    return { minSalary: range.min.toString(), maxSalary: range.max.toString() }
  }

  // Fetch job by ID from URL on mount
  useEffect(() => {
    if (!id) return

    setIsLoading(true)

    getJobById(Number(id))
      .then((jobData: JobPost) => {
        if (!isMountedRef.current) return

        setIsEditing(jobData.id)

        const minSalary =
          jobData.compensation?.min?.toString() ||
          jobData.compensation_min?.toString() ||
          parseSalaryRange(jobData.salary).minSalary

        const maxSalary =
          jobData.compensation?.max?.toString() ||
          jobData.compensation_max?.toString() ||
          parseSalaryRange(jobData.salary).maxSalary

        const minExperience = jobData.experience?.min || jobData.experience_min || ''
        const maxExperience = jobData.experience?.max || jobData.experience_max || ''

        const convertToDate = (timeStr?: string) => {
          if (!timeStr) return null

          const today = new Date()
          const parsed = new Date(`${today.toDateString()} ${timeStr}`)
          return isNaN(parsed.getTime()) ? null : parsed
        }

        setFormData({
          title: jobData.title || '',
          description: jobData.description || '',
          job_type: jobData.job_type || '',
          vacancies: jobData.vacancies || '',
          locations: jobData.locations && jobData.locations.length > 0 
            ? jobData.locations 
            : [{ id: 'default', latitute: '', longitute: '', location_name: '', postcode: '' }],
          salary: `${minSalary},${maxSalary}`,
          experience_max: maxExperience,
          experience_min: minExperience,
          startTime: convertToDate(jobData.timing_from),
          endTime: convertToDate(jobData.timing_to),
          skills: jobData.skills?.map(skill => skill.name) || [],
          status: jobData.status || 'DRAFT'
        })

        setSelectedUsers(jobData.created_by ? {
          value: String(jobData.created_by.id),
          label: `${jobData.created_by.first_name} ${jobData.created_by.last_name} (${jobData.created_by.email}) (${jobData.created_by.phone})`
        } : null)

        // Set selected skills for edit
        if (jobData.skills && jobData.skills.length > 0) {
          setSelectedSkills(jobData.skills.map((skill: any) => ({
            value: String(skill.id),
            label: skill.name
          })))
        }

        setIsFormLoaded(true)
      })
      .catch(console.error)
      .finally(() => {
        if (isMountedRef.current) setIsLoading(false)
      })
  }, [id])

  useEffect(() => {
    fetchUsersList()
    fetchSkills()
  }, [])

  // Removed old skill suggestions useEffect

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Avoid re-mounting third-party editors during React concurrent mode/StrictMode
  useEffect(() => {
    setEditorMounted(true)
  }, [])

  // Removed old selectSkill and removeSkill functions

  const validateForm = () => {
    let newErrors: any = {}

    if (!selectedUsers) {
      newErrors.users = 'User selection is required'
    }

    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Job title is required'
    }

    // Check if description is empty or contains only whitespace/empty HTML
    const isDescriptionEmpty = !formData.description ||
                              formData.description.trim() === '' ||
                              formData.description === '<p>&nbsp;</p>' ||
                              formData.description === '<p></p>' ||
                              /^<p>\s*<\/p>$/.test(formData.description) ||
                              /^<p>&nbsp;<\/p>$/.test(formData.description)

    if (isDescriptionEmpty) {
      newErrors.description = 'Job description is required'
    }

    if (!formData.job_type || formData.job_type === '') {
      newErrors.job_type = 'Please select a valid job type'
    }

    if (!formData.status || formData.status === '') {
      newErrors.status = 'Please select a status'
    }

    if (!formData.vacancies || formData.vacancies === '') {
      newErrors.vacancies = 'Please enter number of vacancies'
    } else {
      const vacanciesNum = parseInt(formData.vacancies, 10)
      if (isNaN(vacanciesNum) || vacanciesNum < 0) {
        newErrors.vacancies = 'Vacancies must be a positive number'
      }
    }

    if (!formData.locations || formData.locations.length === 0) {
      newErrors.locations = 'At least one location required'
    } else {
      // Check each location has a valid location_name
      formData.locations.forEach((loc, index) => {
        if (!loc.location_name || loc.location_name.trim() === '') {
          newErrors[`location_${index}`] = `Location ${index + 1} is required`
        }
      })
    }

    const minSalaryNum = parseFloat(formData.salary.split(',')[0] || '0')
    const maxSalaryNum = parseFloat(formData.salary.split(',')[1] || '0')

    if (!formData.salary.split(',')[0] || minSalaryNum === 0) {
      newErrors.min_salary = 'Min salary is required'
    }

    if (!formData.salary.split(',')[1] || maxSalaryNum === 0) {
      newErrors.max_salary = 'Max salary is required'
    }

    if (minSalaryNum > 0 && maxSalaryNum > 0 && minSalaryNum >= maxSalaryNum) {
      newErrors.max_salary = 'Max salary must be greater than Min salary'
    }

    if (!formData.startTime) newErrors.startTime = 'Start time required'
    if (!formData.endTime) newErrors.endTime = 'End time required'
    if (!formData.experience_min) newErrors.experience_min = 'Min experience required'
    if (!formData.experience_max) newErrors.experience_max = 'Max experience required'

    if (!selectedSkills || selectedSkills.length === 0) {
      newErrors.skills = 'At least one skill is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // const formatTimeToAMPM = (date: Date | null) => {
  //   if (!date) return 'N/A'
  //   return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
  // }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    const [minStr, maxStr] = formData.salary.split(',')
    // const salaryRange: SalaryRange = {
    //   min: Number(minStr?.trim() || 0),
    //   max: Number(maxStr?.trim() || 0),
    // }
    // const salaryJson = JSON.stringify(salaryRange)

    setIsLoading(true)

    const timing_from = formData.startTime ? formData.startTime.toTimeString().split(' ')[0] : ''
    const timing_to = formData.endTime ? formData.endTime.toTimeString().split(' ')[0] : ''

    const { salary, ...restFormData } = formData
    const jobData = { ...restFormData, timing_from, timing_to, compensation_min: minStr?.trim() || '0', compensation_max: maxStr?.trim() || '0', vacancies: String(formData.vacancies), status: formData.status, skills: selectedSkills?.map((s) => s.label) || [], provider_id: selectedUsers?.value }
    console.log('Submitting job data:', jobData);
    if (isEditing) {
      // Update existing job
      updateJob(isEditing, jobData)
        .then(() => {
          console.log('Job updated successfully')
          navigate('/jobs')
        })
        .catch((error) => {
          console.error('Failed to update job:', error)
          setIsLoading(false)
        })
    } else {
      // Create new job
      createJob(jobData)
        .then(() => {
          console.log('Job created successfully')
          navigate('/jobs')
        })
        .catch((error) => {
          console.error('Failed to create job:', error)
          setIsLoading(false)
        })
    }
  }

  const cancelAction = () => {
    navigate('/jobs')
  }

  // --- Location Management ---
  const addLocation = () => {
    const id = Date.now().toString()
    setFormData(prev => ({ ...prev, locations: [...prev.locations, { id, latitute: '', longitute: '', location_name: '', postcode: '' }] }))
  }

  const removeLocation = (id: string) => {
    if (formData.locations.length <= 1) {
      // Cannot remove the last location
      return
    }
    setFormData(prev => ({ ...prev, locations: prev.locations.filter(l => l.id !== id) }))
    autocompleteRefs.current.delete(id)
  }

  const updateLocation = (id: string, key: keyof LocationItem, value: string) => {
    setFormData(prev => {
      const index = prev.locations.findIndex(l => l.id === id)
      if (index === -1) return prev
      const newLocations = [...prev.locations]
      newLocations[index] = { ...newLocations[index], [key]: value }
      return { ...prev, locations: newLocations }
    })
  }

  const handlePlaceChangedForId = (id: string) => {
    const place = autocompleteRefs.current.get(id)?.getPlace()
    if (!place || !place.geometry) {
      console.warn('No geometry found for the selected place')
      return
    }

    const lat = place.geometry.location.lat().toString()
    const lng = place.geometry.location.lng().toString()

    let postalCode = ''
    if (place.address_components) {
      const pcObj = place.address_components.find((c: any) => c.types.includes('postal_code'))
      if (pcObj) postalCode = pcObj.long_name
    }

    setFormData(prev => {
      const index = prev.locations.findIndex(l => l.id === id)
      if (index === -1) return prev
      const newLocations = [...prev.locations]
      newLocations[index] = {
        ...newLocations[index],
        latitute: lat,
        longitute: lng,
        location_name: place.formatted_address || '',
        postcode: postalCode
      }
      return { ...prev, locations: newLocations }
    })
  }

  // Removed unused selectInterest function

  const handleSalaryChange = (type: 'min' | 'max') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFormData((prev) => {
      const current = prev.salary || ''
      const parts = current ? current.split(',') : ['', '']
      parts[type === 'min' ? 0 : 1] = value
      return { ...prev, salary: parts.join(',') }
    })
    // Clear the appropriate error when the field changes
    const errorKey = type === 'min' ? 'min_salary' : 'max_salary'
    if (errors[errorKey]) setErrors({ ...errors, [errorKey]: '' })
  }
  const userOptions = userData
    .filter((user: any) => user.is_active === 1)
    .map((user: any) => ({
      value: String(user.id),
      label: `${user.first_name} ${user.last_name} (${user.email}) (${user.phone})`,
    }))

  const fetchUsersList = async () => {
    try {
      const response = await getUsersListForJob()
      const users = response?.users || []

      setUserData(users)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch users'
      toast.error(msg)
    }
  }

  const fetchSkills = async () => {
    setSkillsLoading(true)
    try {
      const response = await getSkills('limit=1000')
      const skills = response?.data || []
      const options = skills
        .filter((skill: any) => skill.is_active === true || skill.is_active === 1) // Strictly only is_active true/1 skills for dropdown
        .map((skill: any) => ({
          value: String(skill.id),
          label: skill.name,
        }))
      setSkillsOptions(options)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch skills'
      toast.error(msg)
      setSkillsOptions([])
    } finally {
      setSkillsLoading(false)
    }
  }

  const handleUserChange = (selected: OptionType | null) => {
    setSelectedUsers(selected)
    if (errors.users) setErrors({ ...errors, users: '' })
  }

  const handleSkillsChange = (newValue: readonly OptionType[]) => {
    setSelectedSkills(newValue as OptionType[])
  }

  return (
    <>
      <PageTitle>
        {isEditing ? 'Update Job' : 'Create Job'}
      </PageTitle>

      <div className='card mb-10 border-0 shadow-sm'>

        <div className='card-body'>
          <form onSubmit={handleSubmit} noValidate>
            {/* ... all previous fields unchanged ... */}
            <div className='mb-7'>
              <label className='required form-label fw-bold text-black'>User</label>
              <Select
                options={userOptions}
                isSearchable
                name="users"
                className={`react-select ${errors.users ? 'is-invalid' : ''}`}
                value={selectedUsers}
                onChange={handleUserChange}
                placeholder="Search and select user"
              />
              {errors.users && <div className='invalid-feedback'>{errors.users}</div>}
            </div>

            <div className='row mb-7'>
              <div className='col-md-12 mb-3'>
                <div className='d-flex justify-content-between align-items-center'>
                  <label className='required form-label fw-bold'>Job Title</label>
                </div>
                <input
                  type='text'
                  placeholder='Enter job title'
                  className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                  value={formData.title}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value })
                    if (errors.title) setErrors({ ...errors, title: '' })
                  }}
                />
                {errors.title && <div className='invalid-feedback'>{errors.title}</div>}
              </div>
            </div>

            <div className='mb-7'>
              <div className='d-flex justify-content-between align-items-center'>
                <label className='required form-label fw-bold'>Job Description</label>
              </div>
              <div className={errors.description ? 'is-invalid' : ''}>
                {editorMounted && (
                  <CKEditor
                    key={isEditing || 'create'}   // prevent remount crash
                    editor={ClassicEditor as any}
                    data={formData.description}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      setFormData((prev) => ({ ...prev, description: data }))
                      if (errors.description) setErrors((prev: any) => ({ ...prev, description: '' }))
                    }}
                    config={{
                      toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                        'indent', 'outdent', '|',
                        'blockQuote', 'insertTable', 'mediaEmbed', 'undo', 'redo'
                      ]
                    }}
                    onReady={(editor) => {
                      // editorRef.current = editor
                      // editor.editing.view.change((writer) => {
                      //   writer.setStyle('min-height', '200px', editor.editing.view.document.getRoot())
                      // })
                    }}
                  />
                )}
              </div>
              {errors.description && (
                <div className='invalid-feedback'>{errors.description}</div>
              )}
            </div>

            <div className='row mb-7'>
              <div className='col-md-6 mb-3'>
                <label className='required form-label fw-bold'>Job Type</label>
                <select
                  className={`form-select ${errors.job_type ? 'is-invalid' : ''}`}
                  value={formData.job_type}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(e) => {
                    setFormData({ ...formData, job_type: e.target.value })
                    if (errors.job_type) setErrors({ ...errors, job_type: '' })
                  }}
                >
                  <option value=''>Select job type</option>
                  <option value='FULL_TIME'>Full-time</option>
                  <option value='PART_TIME'>Part-time</option>
                  <option value='CASUAL'>Casual</option>
                </select>
                {errors.job_type && <div className='invalid-feedback'>{errors.job_type}</div>}
              </div>
              <div className='col-md-6 mb-3'>
                <div className='d-flex justify-content-between align-items-center'>
                  <label className='required form-label fw-bold'>No. of Vancancies</label>
                </div>
                <input
                  type='text'
                  maxLength={50}
                  placeholder='Enter number of vacancies'
                  className={`form-control ${errors.vacancies ? 'is-invalid' : ''}`}
                  value={formData.vacancies}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(e) => {
                    setFormData({ ...formData, vacancies: e.target.value })
                    if (errors.vacancies) setErrors({ ...errors, vacancies: '' })
                  }}
                />
                {errors.vacancies && <div className='invalid-feedback'>{errors.vacancies}</div>}
              </div>
            </div>

            <div className='mb-7'>
              <label className='required form-label fw-bold'>Skills</label>
              <Select
                isMulti
                options={skillsOptions}
                value={selectedSkills}
                onChange={handleSkillsChange}
                isSearchable
                isLoading={skillsLoading}
                className={`react-select ${errors.skills ? 'is-invalid' : ''}`}
                placeholder="Search and select skills"
                noOptionsMessage={() => 'No skills found'}
              />
              {errors.skills && <div className='invalid-feedback'>{errors.skills}</div>}
            </div>

            <div className='row mb-7'>
              <div className='col-md-6 mb-3'>
                <label className='required form-label fw-bold'>Job Time Start</label>
                <Flatpickr
                  className={`form-control ${errors.startTime ? 'is-invalid' : ''}`}
                  value={formData.startTime || ''}
                  placeholder='Select Start Time'
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(d) => setFormData({ ...formData, startTime: d[0] })}
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'h:i K',
                    allowInput: true,
                  }}
                />
                {errors.startTime && (
                  <div className='invalid-feedback'>{errors.startTime}</div>
                )}
              </div>
              <div className='col-md-6 mb-3'>
                <label className='required form-label fw-bold'>Job Time End</label>
                <Flatpickr
                  className={`form-control ${errors.endTime ? 'is-invalid' : ''}`}
                  value={formData.endTime || ''}
                  placeholder='Select End Time'
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(d) => setFormData({ ...formData, endTime: d[0] })}
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'h:i K',
                    allowInput: true,
                  }}
                />
                {errors.endTime && <div className='invalid-feedback'>{errors.endTime}</div>}
              </div>
            </div>

            {/* --- Locations Section --- */}
            <div className='mb-7'>
              <label className='required form-label fw-bold'>Office Locations</label>
              {formData.locations.map((loc, index) => (
                <div key={loc.id} className='mb-3'>
                  <div className='d-flex align-items-center w-100'>
                    {isLoaded && (
                      <Autocomplete
                        onLoad={(a) => {
                          autocompleteRefs.current.set(loc.id, a)
                        }}
                        onPlaceChanged={() => handlePlaceChangedForId(loc.id)}
                        className='w-100 me-3'
                      >
                        <input
                          type='text'
                          placeholder='Search for office location...'
                          className={`form-control me-2 ${errors[`location_${index}`] ? 'is-invalid' : ''}`}
                          value={loc.location_name}
                          disabled={isEditing !== null && !isFormLoaded}
                          onChange={(e) => {
                            updateLocation(loc.id, 'location_name', e.target.value)
                            // Clear error when user starts typing
                            if (errors[`location_${index}`]) {
                              setErrors({ ...errors, [`location_${index}`]: '' })
                            }
                          }}
                        />
                      </Autocomplete>
                    )}
                    <button
                      type='button'
                      className='btn btn-danger font-12'
                      onClick={() => removeLocation(loc.id)}
                      disabled={(isEditing !== null && !isFormLoaded) || formData.locations.length <= 1}
                      title={formData.locations.length <= 1 ? 'Cannot remove the last location' : 'Remove location'}
                    >
                      Remove
                    </button>
                  </div>
                  {errors[`location_${index}`] && (
                    <div className='invalid-feedback d-block'>{errors[`location_${index}`]}</div>
                  )}
                </div>
              ))}
              <button
                type='button'
                className='btn btn-success font-12'
                onClick={addLocation}
                disabled={isEditing !== null && !isFormLoaded}
              >
                Add Location
              </button>
              {errors.locations && <div className='invalid-feedback d-block'>{errors.locations}</div>}
            </div>

            {/* Min/Max Salary Row */}
            <div className='row mb-9'>
              <div className='col-md-6'>
                <label className='required form-label fw-bold'>Min Compensation</label>
                <input
                  type='number'
                  min='0'
                  placeholder='e.g. 30000'
                  className={`form-control ${errors.min_salary ? 'is-invalid' : ''}`}
                  value={formData.salary.split(',')[0] || ''}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={handleSalaryChange('min')}
                />
                {errors.min_salary && <div className='invalid-feedback'>{errors.min_salary}</div>}
              </div>
              <div className='col-md-6'>
                <label className='required form-label fw-bold'>Max Compensation</label>
                <input
                  type='number'
                  min='0'
                  placeholder='e.g. 60000'
                  className={`form-control ${errors.max_salary ? 'is-invalid' : ''}`}
                  value={formData.salary.split(',')[1] || ''}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={handleSalaryChange('max')}
                />
                {errors.max_salary && <div className='invalid-feedback'>{errors.max_salary}</div>}
              </div>
            </div>

            {/* Min/Max Experience Row */}
            <div className='row mb-9'>
              <div className='col-md-6'>
                <label className='required form-label fw-bold'>Min Experience</label>
                <input
                  type='text'
                  placeholder='e.g. 1 year'
                  className={`form-control ${errors.experience_min ? 'is-invalid' : ''}`}
                  value={formData.experience_min}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(e) => {
                    setFormData({ ...formData, experience_min: e.target.value })
                    if (errors.experience_min) setErrors({ ...errors, experience_min: '' })
                  }}
                />
                {errors.experience_min && <div className='invalid-feedback'>{errors.experience_min}</div>}
              </div>
              <div className='col-md-6'>
                <label className='required form-label fw-bold'>Max Experience</label>
                <input
                  type='text'
                  placeholder='e.g. 5 years'
                  className={`form-control ${errors.experience_max ? 'is-invalid' : ''}`}
                  value={formData.experience_max}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(e) => {
                    setFormData({ ...formData, experience_max: e.target.value })
                    if (errors.experience_max) setErrors({ ...errors, experience_max: '' })
                  }}
                />
                {errors.experience_max && <div className='invalid-feedback'>{errors.experience_max}</div>}
              </div>
            </div>
            <div className='row mb-9'>
              <div className='col-md-6 mb-3'>
                <label className='required form-label fw-bold'>Status</label>
                <select
                  className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                  value={formData.status}
                  disabled={isEditing !== null && !isFormLoaded}
                  onChange={(e) => {
                    setFormData({ ...formData, status: e.target.value })
                    if (errors.status) setErrors({ ...errors, status: '' })
                  }}
                >
                  <option value=''>Select status</option>
                  <option value='ACTIVE'>Active</option>
                  <option value='DRAFT'>Draft</option>
                  <option value='CLOSED'>Closed</option>
                </select>
                {errors.status && <div className='invalid-feedback'>{errors.status}</div>}
              </div>
            </div>

            <div className='d-flex justify-content-end'>
              <button type='button' className='btn btn-light me-3 px-10' onClick={cancelAction}>
                Cancel
              </button>
              <button type='submit' className='btn btn-primary px-12' disabled={isLoading}>
                {isLoading ? 'Processing...' : isEditing ? 'Save Changes' : 'Post Job'}
              </button>
            </div>
          </form>
        </div >
      </div >
    </>
  )
}

export default JobAddPage
